import { Component, Renderer2 } from '@angular/core';
import {
  DatatableActionButton,
  DatatableEvent,
  DatatableHandler,
  DatatableHandlerSettings,
  ErrorHandlingMode,
  IconType,
} from '../datatable/datatable/datatable.component';
import { ActivatedRoute } from '@angular/router';
import { StudentExamDataService } from '../../../services/data-services/student-exam-data.service';
import {
  StudentResultsClient,
  ExamReportResponse,
  ExamReportsClient,
} from '../../../generated-client/generated-api-client';
import { SimpleModalService } from 'ngx-simple-modal';
import { PdfPreviewModalComponent } from '../../modals/pdf-preview-modal/pdf-preview-modal.component';
import { ToastrService } from 'ngx-toastr';
import { DataTableRoutes } from 'src/app/constants/datatableUrls';
import { ErrorProcessorService } from '../../../services/error-processor.service';
import { ConfirmReasonModalComponent } from '../../modals/archive-confirm-modal/confirm-reason-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { DataTableDataService } from '../../../services/data-services/navigation-data-services';
import { Icu } from '@angular/compiler/src/i18n/i18n_ast';
import { DetailedResultV2PreviewModalComponent } from '../../modals/detailed-resultV2-preview-modal/detailed-resultV2-preview-modal.component';

@Component({
  selector: 'app-exam-results-datatable',
  templateUrl: './exam-results-datatable.component.html',
  styleUrls: ['./exam-results-datatable.component.sass'],
})
export class ExamResultsDatatableComponent {
  dtHandler?: DatatableHandler;
  dtSettings: DatatableHandlerSettings = DatatableHandler.getDefaultSettings();
  dtDataSourceUrl: string = DataTableRoutes.ExamStudentResultList;
  studentId!: string;
  tableLabel?: string;
  precision: number = 0;

  constructor(
    private _route: ActivatedRoute,
    private _studentExamDataService: StudentExamDataService,
    private _studentResultsClient: StudentResultsClient,
    private _examReportsClient: ExamReportsClient,
    private _modalService: SimpleModalService,
    private _renderer: Renderer2,
    private _toastr: ToastrService,
    private _errorProcessor: ErrorProcessorService,
    private _dataTableService: DataTableDataService
  ) {
    _examReportsClient.get(this.examReportId).subscribe(next => {
      this.precision = next.percentagePrecision || 0;
    });
    this._route.params.subscribe(next => {
      if (!this.registrationNumber) {
        this.tableLabel = this.studentName;
      } else {
        this.tableLabel = this.registrationNumber + ' - ' + this.studentName;
      }
      this.studentId = next['id'];
      this.dtHandler?.refresh();
    });
    this.dtSettings.errorHandlingMode = ErrorHandlingMode.Redirect;
    this.dtSettings.order = [[0, 'asc']];
    this.dtSettings.additionalRequestData = () => {
      return {
        studentId: this.studentId,
      };
    };
    this.dtSettings.additionalColumns = [
      {
        targets: 'percentagePrecision',
        className: 'percentagePrecision',
        render: data =>
          (Math.trunc(data * 100 * Math.pow(10, this.precision)) / Math.pow(10, this.precision)).toFixed(
            this.precision
          ) + '%',
      },
      {
        targets: 'score',
        className: 'score',
        render: data =>
          (Math.trunc(data * Math.pow(10, this.precision)) / Math.pow(10, this.precision)).toFixed(this.precision),
      },
    ];
    this.dtSettings.actions = (data: any) => {
      const printAction = new DatatableActionButton('preview', 'Előnézet', 'picture_as_pdf');
      const sendAction = new DatatableActionButton('send', 'Küldés', 'send');
      const detailedActionV2 = new DatatableActionButton(
        'json',
        'Részletes eredmény',
        'read_more',
        undefined,
        IconType.Normal,
        data['measure'] == null
      );
      return [printAction, sendAction, detailedActionV2];
    };
  }

  set dataTableToService(dtHandler: DatatableHandler) {
    this._dataTableService.dataTable = dtHandler;
  }

  get studentName() {
    return this._studentExamDataService.studentName.value;
  }

  get registrationNumber() {
    return this._studentExamDataService.registrationNumber.value;
  }

  get examReportId() {
    return this._studentExamDataService.examReportId.value;
  }

  onDtInit(dtHandler: DatatableHandler): void {
    this.dtHandler = dtHandler;
    this.dataTableToService = dtHandler;
    this.dtHandler.create();
  }

  onDtAction(event: DatatableEvent): void {
    if (event.actionId === 'preview') {
      this.previewPdf(event.data['id']);
    } else if (event.actionId === 'json') {
      this.showDetailedResultV2Modal(event.data['id'], event.data['subjectName']);
    } else if (event.actionId === 'send') {
      this.sendResultEmail([event.data['id']]);
    }
  }

  private sendResultEmail(resultId: any): void {
    this._modalService
      .addModal(ConfirmModalComponent, {
        title: 'Tanuló értesítése eredményről',
        message: 'Biztosan ki akarja értesíteni a címzettet a vizsga eredményéről?',
        confirmText: 'Igen',
        confirmClass: 'btn-primary text-white',
      })
      .subscribe(result => {
        if (result) {
          this._studentResultsClient.sendResultEmail(this.studentId, resultId).subscribe(
            next => {
              this._toastr.success('Sikeres email küldés');
              this.dtHandler?.refresh();
            },
            error => {
              this._toastr.error('Sikertelen email küldés');
            }
          );
        }
      });
  }

  private previewPdf(id: string): void {
    this._studentResultsClient.pdfPreview(id).subscribe(next => {
      if (!next.htmlTemplate) {
        this._toastr.error('Nem sikerült a PDF előnézet megjelenítése');
        return;
      }
      const parser = new DOMParser();
      const html = parser.parseFromString(next.htmlTemplate, 'text/html');

      this._modalService.addModal(PdfPreviewModalComponent, {
        content: html.body.outerHTML,
      });
    });
  }

  private showDetailedResultV2Modal(id: string, title: string): void {
    this._modalService.addModal(DetailedResultV2PreviewModalComponent, {
      resultId: id,
      courseTitle: title,
      studentName: this.studentName,
    });
  }
}
